import { Separator } from '@heidi/design-system/separator';
import { type Editor, useEditorState } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import {
  ArrowDown,
  ArrowUp,
  BetweenHorizontalEnd,
  BetweenHorizontalStart,
  BetweenVerticalEnd,
  BetweenVerticalStart,
  Columns2,
  Rows2,
  SquareDashedMousePointer,
  Trash2,
} from 'lucide-react';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import {
  getTableMenuAnchor,
  isDeleteColumnDisabled,
  isDeleteRowDisabled,
  isMoveNodeDisabled,
  moveNode,
  selectTable,
  shouldShowTableMenu,
  tableCellSupportsAlign,
} from '../core';
import { getDialogContainer } from './get-dialog-container';
import { MarkdownTableControls } from './table-menu-markdown-controls';
import { Toolbar } from './toolbar-namespace';

// Visual-only disable: the design-system Button's native `disabled` sets
// `pointer-events-none`, which would also suppress the tooltip's hover — the
// tooltip is how the user learns *why* the action is unavailable. So this
// stays off the `disabled` prop; the click handlers below refuse to act.
const disabledButtonClassName =
  'cursor-not-allowed text-text-disabled hover:bg-transparent hover:text-text-disabled';

export const TableMenu = ({ editor }: { editor: Editor }) => {
  const intl = useIntl();

  const { deleteColumnDisabled, deleteRowDisabled, moveDownDisabled, moveUpDisabled } =
    useEditorState({
      editor,
      selector: ({ editor: instance }: { editor: Editor }) => ({
        deleteColumnDisabled: isDeleteColumnDisabled(instance),
        deleteRowDisabled: isDeleteRowDisabled(instance),
        moveDownDisabled: isMoveNodeDisabled(instance, 'down'),
        moveUpDisabled: isMoveNodeDisabled(instance, 'up'),
      }),
    });

  // TipTap's debounced update can run the predicate after the editor is
  // destroyed; showing then appends the menu via the dead view's DOM.
  const shouldShow = useCallback(
    () => !editor.isDestroyed && shouldShowTableMenu(editor),
    [editor]
  );

  // A custom `shouldShow` replaces the bubble menu's default, focus gate and all,
  // so this can fire over a blurred editor — where the selection paints but copy
  // and Backspace go nowhere. Focused here, not in `selectTable`, which is a
  // kernel command with no DOM opinion.
  const onSelectTable = useCallback(() => {
    editor.commands.focus();
    selectTable(editor);
  }, [editor]);

  const onAddRowBefore = useCallback(() => {
    editor.chain().focus().addRowBefore().run();
  }, [editor]);

  const onAddRowAfter = useCallback(() => {
    editor.chain().focus().addRowAfter().run();
  }, [editor]);

  const onAddColumnBefore = useCallback(() => {
    editor.chain().focus().addColumnBefore().run();
  }, [editor]);

  const onAddColumnAfter = useCallback(() => {
    editor.chain().focus().addColumnAfter().run();
  }, [editor]);

  const onDeleteRow = useCallback(() => {
    if (deleteRowDisabled) return;
    editor.chain().focus().deleteRow().run();
  }, [editor, deleteRowDisabled]);

  const onDeleteColumn = useCallback(() => {
    if (deleteColumnDisabled) return;
    editor.chain().focus().deleteColumn().run();
  }, [editor, deleteColumnDisabled]);

  const onDeleteTable = useCallback(() => {
    editor.chain().focus().deleteTable().run();
  }, [editor]);

  const onMoveUp = useCallback(() => {
    if (moveUpDisabled) return;
    moveNode(editor, 'up');
  }, [editor, moveUpDisabled]);

  const onMoveDown = useCallback(() => {
    if (moveDownDisabled) return;
    moveNode(editor, 'down');
  }, [editor, moveDownDisabled]);

  return (
    // TipTap's default pluginKey is "bubbleMenu"; unregisterPlugin("bubbleMenu")
    // prefix-matches every `bubbleMenu$…` plugin and would drop this menu when
    // a text-menu BubbleMenu remounts on the same editor.
    <BubbleMenu
      pluginKey="tableMenu"
      shouldShow={shouldShow}
      editor={editor}
      appendTo={() => getDialogContainer(editor)}
      getReferencedVirtualElement={() => getTableMenuAnchor(editor)}
      options={{
        placement: 'top-end',
      }}
    >
      <Toolbar.Wrapper
        onMouseDown={(e) => {
          e.preventDefault();
        }}
      >
        <Toolbar.Button
          tooltip={intl.formatMessage({
            defaultMessage: 'Select table',
            id: 'umdltR778F',
          })}
          onClick={onSelectTable}
          type="button"
        >
          <SquareDashedMousePointer size={14} />
        </Toolbar.Button>
        <Separator orientation="vertical" className="mx-0.5 h-4" />
        <Toolbar.Button
          tooltip={intl.formatMessage({
            defaultMessage: 'Add row above',
            id: 'Lag3AXTHvH',
          })}
          onClick={onAddRowBefore}
          type="button"
        >
          <BetweenHorizontalStart size={14} />
        </Toolbar.Button>
        <Toolbar.Button
          tooltip={intl.formatMessage({
            defaultMessage: 'Add row below',
            id: '+MYMhjmHdZ',
          })}
          onClick={onAddRowAfter}
          type="button"
        >
          <BetweenHorizontalEnd size={14} />
        </Toolbar.Button>
        <Toolbar.Button
          tooltip={intl.formatMessage({
            defaultMessage: 'Add column left',
            id: 'hUkzCnbKyR',
          })}
          onClick={onAddColumnBefore}
          type="button"
        >
          <BetweenVerticalStart size={14} />
        </Toolbar.Button>
        <Toolbar.Button
          tooltip={intl.formatMessage({
            defaultMessage: 'Add column right',
            id: 'BnW2uzG9OY',
          })}
          onClick={onAddColumnAfter}
          type="button"
        >
          <BetweenVerticalEnd size={14} />
        </Toolbar.Button>
        <Separator orientation="vertical" className="mx-0.5 h-4" />
        <Toolbar.Button
          tooltip={
            deleteRowDisabled
              ? intl.formatMessage({
                  defaultMessage: 'A table needs at least one row',
                  id: '8gx2A4Xq0f',
                })
              : intl.formatMessage({
                  defaultMessage: 'Delete row',
                  id: 'w19eTCffOW',
                })
          }
          onClick={onDeleteRow}
          aria-disabled={deleteRowDisabled || undefined}
          className={deleteRowDisabled ? disabledButtonClassName : undefined}
          type="button"
        >
          <Rows2 size={14} />
        </Toolbar.Button>
        <Toolbar.Button
          tooltip={
            deleteColumnDisabled
              ? intl.formatMessage({
                  defaultMessage: 'A table needs at least one column',
                  id: 'APtO7HO2ft',
                })
              : intl.formatMessage({
                  defaultMessage: 'Delete column',
                  id: 'qfX6msoX6Z',
                })
          }
          onClick={onDeleteColumn}
          aria-disabled={deleteColumnDisabled || undefined}
          className={deleteColumnDisabled ? disabledButtonClassName : undefined}
          type="button"
        >
          <Columns2 size={14} />
        </Toolbar.Button>
        {tableCellSupportsAlign(editor) && <MarkdownTableControls editor={editor} />}
        <Separator orientation="vertical" className="mx-0.5 h-4" />
        <Toolbar.Button
          tooltip={
            moveUpDisabled
              ? intl.formatMessage({
                  defaultMessage: "The table can't move further up",
                  id: 'Fl06AqHHqV',
                })
              : intl.formatMessage({
                  defaultMessage: 'Move table up',
                  id: 'KqZ4tpsgqn',
                })
          }
          onClick={onMoveUp}
          aria-disabled={moveUpDisabled || undefined}
          className={moveUpDisabled ? disabledButtonClassName : undefined}
          type="button"
        >
          <ArrowUp size={14} />
        </Toolbar.Button>
        <Toolbar.Button
          tooltip={
            moveDownDisabled
              ? intl.formatMessage({
                  defaultMessage: "The table can't move further down",
                  id: '3IkBp3kMJH',
                })
              : intl.formatMessage({
                  defaultMessage: 'Move table down',
                  id: 'b2PdiTeEMO',
                })
          }
          onClick={onMoveDown}
          aria-disabled={moveDownDisabled || undefined}
          className={moveDownDisabled ? disabledButtonClassName : undefined}
          type="button"
        >
          <ArrowDown size={14} />
        </Toolbar.Button>
        <Separator orientation="vertical" className="mx-0.5 h-4" />
        <Toolbar.Button
          tooltip={intl.formatMessage({
            defaultMessage: 'Delete table',
            id: '2CyUQrc7O4',
          })}
          onClick={onDeleteTable}
          type="button"
        >
          <Trash2 size={14} />
        </Toolbar.Button>
      </Toolbar.Wrapper>
    </BubbleMenu>
  );
};
